import { API_BASE_URL } from "../../constants/index";
import HttpInterface from "../../helpers/HttpInterface";
import { defaultFetchParam, FetchParam } from "../../Models/FetchParam";
import {
  CreateParcelTemplate,
  UpdateParcelTemplate,
} from "../../Models/ParcelTemplate";
import { buildAPIUrl } from "../../utilities/api";

export default class ParcelTemplatesService {
  private httpProxy: HttpInterface;

  constructor() {
    this.httpProxy = new HttpInterface();
  }

  /**
   * Fetches all parcel templates from the shipment service API.
   *
   * @param params The query parameters for the API request. Defaults to `defaultFetchParam` if not provided.
   * @returns A promise that resolves with the API response containing all parcel templates.
   */
  all(params: FetchParam = defaultFetchParam): Promise<any> {
    const requestOptions = {
      method: "GET",
    };

    return this.httpProxy.fetch(
      buildAPIUrl(`${API_BASE_URL}/shipment-service/parcel-templates`, params),
      requestOptions
    );
  }

  /**
   * Fetches a specific parcel templates by its ID from the API.
   *
   * @param id The unique identifier of the parcel templates to fetch.
   * @returns A promise that resolves with the API response containing the parcel templates details.
   */
  get(id: string): Promise<any> {
    const requestOptions = {
      method: "GET", // HTTP method for the request
    };

    // Constructs the URL for fetching a specific parcel templates by its ID
    return this.httpProxy.fetch(
      `${API_BASE_URL}/shipment-service/parcel-templates/${id}`,
      requestOptions
    );
  }

  /**
   * Creates a new parcel template in the shipment service API.
   *
   * @param parcel template The details of the parcel template to create.
   * @returns A promise that resolves with the API response confirming the creation of the parcel template.
   */
  create(parcelTemplate: CreateParcelTemplate): Promise<any> {
    const requestOptions = {
      method: "POST",
      body: JSON.stringify(parcelTemplate),
    };

    // Sends a POST request to create the new parcel template
    return this.httpProxy.fetch(
      `${API_BASE_URL}/shipment-service/parcel-templates`,
      requestOptions
    );
  }

  /**
   * Updates an existing parcel template in the shipment service API.
   *
   * @param parcel template The details of the parcel template to create.
   * @returns A promise that resolves with the API response confirming the creation of the parcel template.
   */
  edit(parcelTemplate: UpdateParcelTemplate): Promise<any> {
    const requestOptions = {
      method: "PUT",
      body: JSON.stringify(parcelTemplate),
    };
    return this.httpProxy.fetch(
      API_BASE_URL + `/shipment-service/parcel-templates/${parcelTemplate.id}`,
      requestOptions
    );
  }

  /**
   * Deletes a parcel template from the shipment service API.
   *
   * @param id The unique identifier of the parcel template to delete.
   * @returns A promise that resolves with the API response confirming the deletion.
   */
  delete(id: string): Promise<any> {
    const requestOptions = {
      method: "DELETE",
    };
    return this.httpProxy.fetch(
      API_BASE_URL + `/shipment-service/parcel-templates/${id}`,
      requestOptions
    );
  }
}
